import { fromWebFile, type UploadableImageAsset } from '@/lib/image-upload';
import type { PhotoSource } from './photo-types';

export async function selectPhotoSource(signal: AbortSignal): Promise<PhotoSource | null> {
  const file = await new Promise<File | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.style.display = 'none';
    let settled = false;
    const finish = (value: File | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      signal.removeEventListener('abort', cancel);
      resolve(value);
    };
    const cancel = () => finish(null);
    input.onchange = () => finish(input.files?.[0] ?? null);
    input.addEventListener('cancel', cancel, { once: true });
    signal.addEventListener('abort', cancel, { once: true });
    document.body.appendChild(input);
    if (signal.aborted) cancel();
    else input.click();
  });
  if (!file || signal.aborted) return null;
  const asset = fromWebFile(file);
  const release = () => URL.revokeObjectURL(asset.uri);
  signal.addEventListener('abort', release, { once: true });
  try {
    const image = new window.Image();
    image.src = asset.uri;
    await image.decode();
    if (signal.aborted) { URL.revokeObjectURL(asset.uri); return null; }
    return { asset, width: image.naturalWidth, height: image.naturalHeight, release };
  } catch (error) {
    URL.revokeObjectURL(asset.uri);
    if (signal.aborted) return null;
    throw error;
  } finally {
    signal.removeEventListener('abort', release);
  }
}

export async function releasePhotoAsset(_asset: UploadableImageAsset) {
  // Cropped web outputs are Blobs, without an object URL to retain/revoke.
}
